/**
 * Single source of truth for product identity.
 *
 * Everything user-visible that names the product flows from here: the document
 * title, the header wordmark, export filenames, the storage-key prefix, the
 * JSON interchange `format` discriminator and the source-file extension.
 * Renaming the product is a one-file change.
 *
 * Note: `storagePrefix` and `fileFormatId` participate in persisted data. If you
 * change them, previously saved projects and previously exported `.json` files
 * stop being recognised — see `ACCEPTED_FORMAT_IDS` in `src/export/json.ts` for
 * the compatibility hook.
 */
export interface BrandingConfig {
  /** Display name, e.g. in the header and the document title. */
  readonly name: string;
  /** Short tagline shown on the welcome screen. */
  readonly tagline: string;
  /** Longer description for the welcome screen / meta description. */
  readonly description: string;
  /** Lowercase slug used for storage keys and default file names. */
  readonly slug: string;
  /** Extension (with dot) for pseudocode source files. */
  readonly sourceExtension: string;
  /** Extra source extensions accepted on import. */
  readonly acceptedSourceExtensions: readonly string[];
  /** Discriminator written into exported JSON documents. */
  readonly fileFormatId: string;
  /** Prefix for localStorage keys and the IndexedDB database name. */
  readonly storagePrefix: string;
  /** Optional link shown in the help dialog; empty string hides it. */
  readonly repositoryUrl: string;
}

export const BRANDING: BrandingConfig = {
  name: 'FlowScript',
  tagline: 'Write the logic. Watch the flowchart.',
  description:
    'Write pseudocode on the left and watch the flowchart build itself on the right. Everything runs in your browser — no account, no server, no upload.',
  slug: 'flowscript',
  sourceExtension: '.flow',
  acceptedSourceExtensions: ['.flow', '.txt', '.fs'],
  fileFormatId: 'flowscript',
  storagePrefix: 'flowscript',
  repositoryUrl: '',
};

/** Namespaced key for `localStorage` / `sessionStorage`. */
export function storageKey(suffix: string): string {
  return `${BRANDING.storagePrefix}.${suffix}`;
}

/**
 * Turns a project name into a filesystem-safe base filename.
 * Falls back to the product slug when the name has no usable characters.
 */
export function exportFileName(projectName: string, extension: string): string {
  const base = projectName
    .normalize('NFKD')
    // Drop the combining marks NFKD just split off, so "Fluxo Início" -> "Fluxo-Inicio".
    .replace(/\p{M}+/gu, '')
    .replace(/[^\w\-. ]+/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 64);
  const safe = base.length > 0 ? base : BRANDING.slug;
  return `${safe}${extension.startsWith('.') ? extension : `.${extension}`}`;
}
